import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, TOKEN_KEY, setUnauthorizedHandler } from "@/lib/api";
import { LEGAL_VERSIONS } from "@/config/legalVersions";
import { recordLocalConsent } from "@/lib/consent";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

// Maps the Travel-Matching user shape onto the fields the web UI consumes today.
// Keeps every native TM field (spread) and adds compatibility aliases so existing
// components (AppShell etc.) keep working until later phases refactor them.
export function mapTmUser(tm) {
  if (!tm) return null;
  return {
    ...tm,
    displayName: tm.first_name || tm.email || "",
    photoURL: tm.profile_image || null,
    premium: tm.premium_active ?? (Boolean(tm.premium_status) && tm.premium_status !== "free"),
    age: tm.age ?? tm.calculated_age ?? null,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  const sessionExpiredShown = useRef(false);

  // Local-only teardown of auth state + cached protected data. Reused by logout and
  // by the global 401 handler. Never calls the backend itself.
  const clearAuthState = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    queryClient.cancelQueries();
    queryClient.clear();
  }, [queryClient]);

  // Global 401 handler: an expired/invalid session (e.g. an old token without `sid`,
  // which the backend now rejects with 401) drops the session cleanly, redirects to
  // login (via ProtectedRoute once user==null) and shows a single hint (no loop/spam).
  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearAuthState();
      if (!sessionExpiredShown.current) {
        sessionExpiredShown.current = true;
        toast.error("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.");
      }
    });
  }, [clearAuthState]);

  // Firebase/dev-login sind im TM-Web-Client deaktiviert. Google-Login läuft
  // über den direkten Google-OAuth-Flow des Voyalyo-Backends
  // -> Travel-JWT), identisch zum E-Mail-Login (kein Cookie, kein Google-Client im Frontend).
  const platform = {
    firebaseConfigured: false,
    firebaseAuthActive: false,
    googleAuthActive: true,
    devAuthEnabled: false,
    revenueCatEnabled: false,
    environment: "development",
  };

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      const u = mapTmUser(data);
      setUser(u);
      return u;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      // Bei Rückkehr aus dem direkten Google-OAuth-Callback NICHT vorab /auth/me laden.
      // Das kurzlebige Einmal-Ticket wird zuerst gegen unser JWT getauscht.
      if (
        typeof window !== "undefined" &&
        window.location.pathname === "/auth/google/callback" &&
        new URLSearchParams(window.location.search).has("ticket")
      ) {
        setLoading(false);
        return;
      }
      if (localStorage.getItem(TOKEN_KEY)) await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  // Direkter Google-OAuth-Flow über das eigene Voyalyo-Backend.
  const loginWithGoogleRedirect = () => {
    const returnTo = window.location.origin + "/auth/google/callback";
    window.location.href =
      `https://api.voyalyo.de/api/auth/google/start?return_to=${encodeURIComponent(returnTo)}`;
  };

  // Kurzlebiges Google-Ticket gegen das eigene Voyalyo-JWT tauschen.
  const exchangeGoogleTicket = async (ticket) => {
    const { data } = await api.post("/auth/google/exchange", { ticket });

    localStorage.setItem(TOKEN_KEY, data.access_token);
    sessionExpiredShown.current = false;

    queryClient.cancelQueries();
    queryClient.clear();

    const u = await refresh();

    return {
      user: u,
      onboardingComplete:
        data.onboarding_complete ?? u?.onboarding_complete ?? u?.onboardingComplete
    };
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    sessionExpiredShown.current = false;
    // Neuer Login -> alten Query-Cache verwerfen, dann Vollprofil via /auth/me laden, damit das
    // Restriction-Gating (ProtectedRoute/AppShell) sofort korrekt ist – ohne Flash/Stale-State.
    queryClient.cancelQueries();
    queryClient.clear();
    const u = (await refresh()) || mapTmUser(data.user);
    return u;
  };

  const register = async ({ email, password, firstName, lastName, birthDate, termsAccepted, privacyAcknowledged, turnstileToken }) => {
    const { data } = await api.post("/auth/register", {
      email,
      password,
      first_name: firstName,
      last_name: lastName,
      birth_date: birthDate,
      // Getrennte Rechts-Bestätigungen (siehe Backend-Contract-Doku). accepted_terms
      // bleibt als Rückwärtskompatibilität erhalten und spiegelt die AGB-Zustimmung.
      accepted_terms: Boolean(termsAccepted),
      terms_accepted: Boolean(termsAccepted),
      privacy_acknowledged: Boolean(privacyAcknowledged),
      terms_version: LEGAL_VERSIONS.terms,
      privacy_version: LEGAL_VERSIONS.privacy,
      turnstile_token: turnstileToken,
    });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    sessionExpiredShown.current = false;
    const u = (await refresh()) || mapTmUser(data.user);
    // Lokaler Nachweis (Fallback), bis das Backend die Consent-Felder persistiert.
    recordLocalConsent(u, "email");
    return u;
  };

  // Consent nach Social Login ("Rechtliche Bestätigung"). Übermittelt beide getrennten
  // Bestätigungen + Versionen an das Backend. HINWEIS: Der serverseitige Endpunkt
  // POST /auth/consent ist Teil des noch offenen Backend-Contracts – ist er nicht
  // verfügbar, wird der Nachweis lokal protokolliert, damit der Flow im Preview
  // vollständig testbar bleibt (siehe Report / fehlender Backend-Contract).
  const submitConsent = async (provider) => {
    const payload = {
      terms_accepted: true,
      privacy_acknowledged: true,
      terms_version: LEGAL_VERSIONS.terms,
      privacy_version: LEGAL_VERSIONS.privacy,
      registration_provider: provider,
    };
    let backendAccepted = false;
    try {
      await api.post("/auth/consent", payload);
      backendAccepted = true;
    } catch (err) {
      console.warn(
        "[consent] Backend-Endpunkt POST /auth/consent nicht verfügbar – Consent wird nur lokal protokolliert.",
        err?.response?.status
      );
    }
    recordLocalConsent(user, provider);
    await refresh().catch(() => {});
    return { backendAccepted };
  };

  // Sends a password-reset request. Never reveals whether the account exists.
  const forgotPassword = async (email, turnstileToken) => {
    await api.post("/auth/forgot-password", { email, turnstile_token: turnstileToken });
  };

  // Fordert eine neue Bestätigungs-E-Mail an (Travel-Contract: POST /auth/verify-email/resend
  // { email }). Cooldown/Rate-Limit-Behandlung erfolgt in der aufrufenden UI.
  const resendVerificationEmail = async (email) => {
    await api.post("/auth/verify-email/resend", { email });
  };

  // Sets a new password using the token from the reset email. Token is never stored/logged.
  const resetPassword = async (token, newPassword) => {
    await api.post("/auth/reset-password", { token, new_password: newPassword });
  };

  // Changes the password via the backend. The current session stays valid; all OTHER
  // sessions are revoked server-side. The backend returns a fresh JWT for the current
  // session which we adopt so the current device is not logged out. Returns the raw
  // response ({ ok, sessions_invalidated, access_token, message }).
  const changePassword = async (currentPassword, newPassword) => {
    const { data } = await api.post("/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
    if (data?.access_token) localStorage.setItem(TOKEN_KEY, data.access_token);
    await refresh();
    return data;
  };

  // Setzt NUR die Profildaten zurück (Konto/Anmeldung/Verifizierungen/Premium bleiben).
  // Backend-Contract: POST /auth/profile-reset { delete_trips?: boolean }. Danach /auth/me
  // neu laden (Onboarding-Status = Source of Truth); der Nutzer bleibt eingeloggt.
  const profileReset = async (deleteTrips = false) => {
    await api.post("/auth/profile-reset", { delete_trips: Boolean(deleteTrips) });
    sessionExpiredShown.current = false;
    const u = await refresh();
    return u;
  };

  // Löscht das Konto dauerhaft (DELETE /auth/account). Lokaler Teardown NUR nach
  // bestätigtem Erfolg des Backends – bei Fehler bleibt die Session vollständig erhalten.
  const deleteAccount = async () => {
    await api.delete("/auth/account");
    try { sessionStorage.clear(); } catch { /* noop */ }
    clearAuthState();
  };

  // Server-side logout: revokes the current session (POST /auth/logout) first, then
  // tears down local state regardless of network outcome (never crashes / leaves a
  // half-logged-in state). Query-based polling stops once protected views unmount.
  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Ignore network/401 errors – we log out locally anyway.
    }
    clearAuthState();
  }, [clearAuthState]);

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, register, submitConsent, forgotPassword, resendVerificationEmail, resetPassword, changePassword, profileReset, deleteAccount, logout, refresh, platform, loginWithGoogleRedirect, exchangeGoogleTicket }}>
      {children}
    </AuthCtx.Provider>
  );
}
